import { useState, useMemo } from 'react';
import { cn } from '../../lib/cn';
import SentenceVisualization from './SentenceVisualization';
import { adaptParseData } from './parseDataAdapter';

/**
 * Tabbed view of a parsed sentence: parts of speech, phrases, clauses.
 * The Phrases and Clauses tabs use the rich SentenceVisualization
 * (curving arrows, color-coded underlines, collapsible regions).
 * The POS tab also uses the visualization but without collapse toggles —
 * keeps focus on the per-token highlight + label layout.
 */
const LAYERS = [
  { key: 'pos',     label: 'Parts of Speech' },
  { key: 'phrases', label: 'Phrases' },
  { key: 'clauses', label: 'Clauses' },
];

export default function ParseLayers({ analysis }) {
  const [active, setActive] = useState('phrases');
  const parse = analysis?.parse_data || {};
  const tokens = parse.tokens || [];

  // Adapt server-side parse_data into the legacy visualization shape.
  // Memoized on the underlying parse_data so collapse/select state inside the
  // visualization doesn't get reset on tab toggles.
  const data = useMemo(
    () => adaptParseData(parse, analysis?.text || ''),
    [analysis?.id, analysis?.text] // eslint-disable-line react-hooks/exhaustive-deps
  );

  if (tokens.length === 0) return null;

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
      <div className="flex border-b border-gray-200 dark:border-gray-700">
        {LAYERS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setActive(key)}
            className={cn(
              'px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] transition-colors',
              active === key
                ? 'text-gray-900 dark:text-white border-b-2 border-gray-900 dark:border-white -mb-[1px]'
                : 'text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200'
            )}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="p-3">
        <SentenceVisualization
          data={data}
          activeLayer={active}
          showCollapseToggles={active !== 'pos'}
        />
      </div>

      {parse.summary && (
        <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-800 text-sm text-gray-600 dark:text-gray-400 italic">
          {parse.summary}
        </div>
      )}
    </div>
  );
}
